import _ from 'lodash';
import { useState } from 'react';

import {
  ArrowDropDown,
  ArrowDropUp
} from '@mui/icons-material';
import {
  Box,
  Button,
  ButtonGroup,
  ButtonProps,
  Stack,
  Typography,
  useTheme
} from '@mui/material';

import { TimeFrame } from 'generated/graphql';

import { StockPriceLineChart } from 'modules/components/layer10';
import {
  CompanyKeyInformationSection,
  CompanyOverviewSection,
  CompanyRevenueBreakdownSection
} from 'modules/components/layer35';
import { CompanyTabLayout } from 'modules/components/layer42';

import { useParams } from 'react-router-dom';
import * as ds from './dataService';



export const FC = () => {
  const params = useParams();
  const theme = useTheme();
  const [
    filter,
    setFilter
  ] = useState<TimeFrame>('oneYear');

  const handleUpdateFilter = (newFilter: TimeFrame) => {
    setFilter((prev) => {
      if (prev === newFilter) {
        return prev;
      } else {
        return newFilter;
      }
    });
  };

  const { data } = ds.useQuery(params.companyId);

  const listedCompanyId = params.companyId;
  if (!listedCompanyId) {
    return null;
  }
  return (
    <CompanyTabLayout.FC
      buttonGroup={(
        <StockPriceButtonGroup
          activeFilter={filter}
          handleUpdateFilter={handleUpdateFilter}
        />
      )}

      summaryMetric={(
        <Box>
          <Stack
            direction='row'
            alignItems='center'
            columnGap='.25rem'
          >
            <Typography variant='activityHeader'>
              {data.currentStockPrice?.toFixed(2) ?? '-'}
            </Typography>

            {!_.isNil(data.changePercent) ? (
              data.changePercent?.gt(0)
                ? (<ArrowDropUp color='success' />)
                : (<ArrowDropDown color='error' />)
            ) : '-'}

            {!_.isNil(data.changePercent) ? (
              <Typography
                variant='styleTextChip'
                color={data.changePercent.gt(0)
                  ? theme.palette.primary.main
                  : theme.palette.error.main}
              >
                {data.changePercent.mul(100).toFixed(2)}%
              </Typography>
            ) : (
              <Typography variant='styleTextChip'>
                -
              </Typography>
            )}
          </Stack>

          {data.stockPriceDate && (
            <Typography
              variant='styleTextChip'
              color={theme.palette.gray.light30}
            >
              {`At close: ${data.stockPriceDate?.toFormat('LLL dd, yyyy')}`}
            </Typography>
          )}
        </Box>
      )}

      companyChart={(
        <StockPriceLineChart.FC
          timeFilter={filter}
          listedCompanyId={listedCompanyId}
        />
      )}

      detailMetricSection={(
        <Stack
          rowGap='2.5rem'
          width='100%'
        >
          <Stack
            direction='row'
            columnGap='2.5rem'
          >
            <CompanyKeyInformationSection.FC listedCompanyId={listedCompanyId} />
            <CompanyOverviewSection.FC listedCompanyId={listedCompanyId} />
          </Stack>

          <Stack
            direction='row'
            columnGap='2.5rem'
          >
            <CompanyRevenueBreakdownSection.FC listedCompanyId={listedCompanyId} />
          </Stack>
        </Stack>
      )}
    />
  );
};

type StockPriceButtonGroupProps = {
  activeFilter: TimeFrame;
  handleUpdateFilter: (newFilter: TimeFrame) => void;
};

const StockPriceButtonGroup = (props: StockPriceButtonGroupProps) => {
  return (
    <ButtonGroup
      variant='outlined'
      color='secondary'
    >
      <Button
        onClick={() => props.handleUpdateFilter('oneMonth')}
        {...__determineButtonState(props.activeFilter === 'oneMonth')}
      >
        1M
      </Button>
      <Button
        onClick={() => props.handleUpdateFilter('threeMonths')}
        {...__determineButtonState(props.activeFilter === 'threeMonths')}
      >
        3M
      </Button>
      <Button
        onClick={() => props.handleUpdateFilter('sixMonths')}
        {...__determineButtonState(props.activeFilter === 'sixMonths')}
      >
        6M
      </Button>
      <Button
        onClick={() => props.handleUpdateFilter('oneYear')}
        {...__determineButtonState(props.activeFilter === 'oneYear')}
      >
        1Y
      </Button>
      <Button
        onClick={() => props.handleUpdateFilter('threeYears')}
        {...__determineButtonState(props.activeFilter === 'threeYears')}
      >3Y
      </Button>
      <Button
        onClick={() => props.handleUpdateFilter('fiveYears')}
        {...__determineButtonState(props.activeFilter === 'fiveYears')}
      >5Y
      </Button>
    </ButtonGroup>
  );
};


type IDetermineButtonState = {
  color: ButtonProps['color'];
  variant: ButtonProps['variant'];
};

function __determineButtonState(active: boolean): IDetermineButtonState {
  return {
    color: active
      ? 'primary'
      : 'secondary',
    variant: active
      ? 'contained'
      : 'contained'
  };
}
